/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { CampaignType, JourneySummary } from '@dev/ee-api/ee-api-models';
import { push as Push } from 'redux-first-history';
import { Route, Switch, withRouter } from 'react-router-dom';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';
import useMobile from '@/services/hooks/useMobile';
import {
    Container,
    TabsPanel,
} from '@dev/ee-components';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { findIndex } from 'lodash-es';
import { ClearCampaignsReportsData } from '@/store/reports/actions';
import {
    ClearPickedCampaign,
    SetAbSplitCurrentTemplate,
    SetAbSplitDisplayMode,
    SetPickedContactEmail,
} from '@/store/campaigns/actions';
import { ApplicationState } from '@/store';
import { AutomationPaths, CampaignsPaths } from '@/services/dictionaries/Paths';
import { CampaignsInterface } from '@/store/campaigns/types';

import SingleContactOverlay from '../../SingleContactOverlay';
import Preview from '../Preview';
import CampaignReportAbSplit from './AbSplit';
import Activity from './Activity';
import Geography from './Geography';
import Links from './Links';
import Overview from './Overview';
import { CampaignReportProps, TabNameEnum } from './CampaignReportInterface';
import { StyledProps } from '../CampaignsInterface';
import { AdditionalHeader } from './components/AccitionalHeader';

const getTabIndexFromName = (tabName: TabNameEnum, type?: CampaignType): number => {
    const tabs = [
        ...(type === CampaignType.AbSplit ? [TabNameEnum.AbSplit] : []),
        TabNameEnum.Overview,
        TabNameEnum.Activity,
        TabNameEnum.Links,
        TabNameEnum.Geography,
    ];
    const index = findIndex(tabs, (name) => name === tabName);

    return index >= 0 ? index : 0;
};

const getTabName = (tabIndex: number, type: CampaignType): TabNameEnum => {
    const tabs = [
        ...(type === CampaignType.AbSplit ? [TabNameEnum.AbSplit] : []),
        TabNameEnum.Overview,
        TabNameEnum.Activity,
        TabNameEnum.Links,
        TabNameEnum.Geography,
    ];

    return tabs[tabIndex];
};

const HeaderContainer = styled.div<StyledProps>`
    div div {
        .MuiGrid-container {
            justify-content: flex-start;
            display: flex;
            width: 100%;
            flex-direction: ${(props): string => (props.isMobile ? 'column' : 'row')};
            align-items: ${(props): string => (props.isMobile ? 'flex-start' : 'center')};
            .MuiGrid-item {
                flex-basis: 0;
                margin-right: 16px;

                & + div {
                    display: flex;
                    flex-grow: 1;
                    flex-direction: ${(props): string => (props.isMobile ? 'column' : 'row')};
                    align-items: ${(props): string => (props.isMobile ? 'flex-start' : 'center')};
                    justify-content: space-between;

                    & > span:not([value]) {
                        margin-bottom: ${(props): string => (props.isMobile ? '20px' : '0')};
                        border: none;
                    }
                }
            }
        }
    }
`;

interface ConditionalHeaderContainerProps {
    condition: boolean;
    isMobile: boolean;
    children: any;
}

const ConditionalHeaderContainer = (
    props: ConditionalHeaderContainerProps
): JSX.Element => {
    const { isMobile, condition } = props;
    return condition ? <HeaderContainer isMobile={isMobile}>{props.children}</HeaderContainer> : props.children;
};

type Props = CampaignReportProps & StateToProps & DispatchProps;

const CampaignReport = (props: Props): JSX.Element => {
    const isMobile = useMobile();
    const isSavedRef = React.useRef(true);

    const historyBackAction = useHistoryBackAction({
        isSavedRef
    });

    const onTabsChange = (e: any, clickedTabIndex: number): void => {
        const { type, name } = props.match.params;
        props.Push(
            CampaignsPaths.CampaignReport.replace(':type', type)
                .replace(':name', name)
                .replace(':tab?', getTabName(clickedTabIndex, type))
        );
    }

    const tabNames = (): { label: string }[] => {
        const { t, match } = props;

        return [
            ...(match.params.type === CampaignType.AbSplit ? [{ label: t('abresults') }] : []),
            {
                label: t('overview'),
            },
            {
                label: t('activity'),
            },
            {
                label: t('links'),
            },
            {
                label: t('geography'),
            },
        ];
    }

    const getBreadcrumbsScreenName = (parentScreenType: CampaignType): string => {
        const { t, singleAutomation, campaigns } = props;

        const automationName = singleAutomation?.Name;
        const isFromAutomation = campaigns?.pickedCampaign?.campaign?.FromAutomation;
        const automationBreadcrumb = automationName ? automationName : t('automations');

        switch (parentScreenType) {
            case CampaignType.SendNow:
                return !isFromAutomation ? t('campaigns') : automationBreadcrumb;
            case CampaignType.Automated:
                return t('autoresponder');
            default:
                return t('campaigns');
        }
    };

    const getBreadcrumbsUri = (parentScreenType: CampaignType): string => {
        const { singleAutomation, campaigns } = props;

        const isFromAutomation = campaigns?.pickedCampaign?.campaign?.FromAutomation;
        const automationName = singleAutomation?.Name;
        const automationBreadcrumb = automationName
            ? AutomationPaths.AutomationReport.replace(':name', safelyEncodeBase64Url(automationName))
            : AutomationPaths.AutomationsList;

        switch (parentScreenType) {
            case CampaignType.SendNow:
                return !isFromAutomation ? CampaignsPaths.CampaignsTab : automationBreadcrumb;
            case CampaignType.Automated:
                return CampaignsPaths.AutorespondersTab;
            default:
                return CampaignsPaths.CampaignsTab;
        }
    };
   
    useEffect(() => {
            props.ClearPickedCampaign();
            props.ClearCampaignsReportsData();
    },[])

    
    const { campaigns, Push, match } = props;

    const additionalHeaderContent = AdditionalHeader(match.params.type);
    const contactEmail = campaigns?.pickedContactEmail;
    const resetContactEmail = (): void => {
        props.SetPickedContactEmail(null);
    };
    const isAbSplit = match.params.type === CampaignType.AbSplit;
    const isAbResultsTab = match.params.tab === TabNameEnum.AbSplit;
    const showTabsPanelHeader = !(isMobile && isAbSplit && !isAbResultsTab);
 
    
    return (
        <HistoryBackActionContext.Provider value={historyBackAction}>
            <Container>
                <ConditionalHeaderContainer
                    condition={match.params.type === CampaignType.Automated}
                    isMobile={isMobile}
                >
                    <TabsPanel
                        breadcrumbs={getBreadcrumbsScreenName(match.params.type)}
                        breadcrumbsOnClick={() => {
                            Push(getBreadcrumbsUri(match.params.type));
                        }}
                        titleSmColumnSizeNumber={10}
                        additionalHeaderContent={showTabsPanelHeader && additionalHeaderContent}
                        title={safelyDecodeBase64Url(match.params.name)}
                        isMobile={isMobile}
                        value={getTabIndexFromName(match.params.tab, match.params.type)}
                        onChange={onTabsChange.bind(this)}
                        tabs={tabNames()}
                        disableSentenceCase
                    />
                    {!showTabsPanelHeader && additionalHeaderContent}
                </ConditionalHeaderContainer>
                <Switch>
                    {match.params.type === CampaignType.AbSplit && (
                        <Route exact component={CampaignReportAbSplit} path={CampaignsPaths.CampaignAbResults} />
                    )}
                    <Route exact component={Overview} path={CampaignsPaths.CampaignOverview} />
                    <Route exact component={Activity} path={CampaignsPaths.CampaignActivity} />
                    <Route exact component={Links} path={CampaignsPaths.CampaignLinks} />
                    <Route exact component={Geography} path={CampaignsPaths.CampaignGeography} />
                </Switch>

                {campaigns.currentPreview.campaign && campaigns.currentPreview.templates?.length && <Preview />}
                {campaigns.pickedContactEmail && (
                    <SingleContactOverlay email={contactEmail} action={resetContactEmail} />
                )}
            </Container>
        </HistoryBackActionContext.Provider>
    );
}


interface StateToProps {
    campaigns: CampaignsInterface;
    singleAutomation?: JourneySummary;
}

interface DispatchProps {
    Push: (url: string) => void;
    ClearPickedCampaign: () => void;
    ClearCampaignsReportsData: () => void;
    SetPickedContactEmail: (email: string) => void;
}

const dispatchedActions = {
    Push,
    ClearPickedCampaign,
    SetAbSplitDisplayMode,
    SetAbSplitCurrentTemplate,
    ClearCampaignsReportsData,
    SetPickedContactEmail,
};

const mapStateToProps = (state: ApplicationState, ownProps: CampaignReportProps): StateToProps => ({
    ...ownProps,
    campaigns: state.campaigns,
    singleAutomation: state.automations.singleAutomation,
});

export default compose(
    withTranslation(),
    withRouter,
    connect<StateToProps, DispatchProps, CampaignReportProps>(mapStateToProps, dispatchedActions)
)(CampaignReport);
